import React, { useState } from 'react';
import { useCourseCreation } from '../../../contexts/CourseCreationContext';
import {
  Plus,
  Trash2,
  Save,
  Eye,
  AlertCircle,
  Play
} from 'lucide-react';

const InteractiveEditor = ({ content, onUpdate, onSave, onPreview }) => {
  const { validateContent } = useCourseCreation();
  const [errors, setErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [activeTab, setActiveTab] = useState('slides');

  const handleFieldChange = (field, value) => {
    const updatedContent = { ...content, [field]: value };
    onUpdate(updatedContent);
    
    const validation = validateContent(updatedContent, 'interactive');
    setErrors(validation.errors);
  };

  const handleSave = async () => {
    setIsValidating(true);
    const validation = validateContent(content, 'interactive');
    
    if (validation.valid) {
      await onSave(content);
      setErrors([]);
    } else {
      setErrors(validation.errors);
    }
    setIsValidating(false);
  };

  const addSlide = () => {
    const newSlide = {
      id: Date.now(),
      title: `Slide ${(content.slides?.length || 0) + 1}`,
      content: 'Contenu de votre slide...',
      image: '',
      code: ''
    };
    
    const updatedSlides = [...(content.slides || []), newSlide];
    handleFieldChange('slides', updatedSlides);
  };

  const updateSlide = (slideId, updates) => {
    const updatedSlides = (content.slides || []).map(slide =>
      slide.id === slideId ? { ...slide, ...updates } : slide
    );
    handleFieldChange('slides', updatedSlides);
  };

  const removeSlide = (slideId) => {
    const updatedSlides = (content.slides || []).filter(slide => slide.id !== slideId);
    handleFieldChange('slides', updatedSlides);
  };

  const addQuizQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      question: 'Votre question ?',
      options: ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
      correct: 0,
      explanation: 'Explication de la réponse correcte'
    };
    
    const updatedQuestions = [...(content.quiz?.questions || []), newQuestion];
    handleFieldChange('quiz', { ...content.quiz, questions: updatedQuestions });
  };

  const updateQuizQuestion = (questionId, updates) => {
    const updatedQuestions = (content.quiz?.questions || []).map(question =>
      question.id === questionId ? { ...question, ...updates } : question
    );
    handleFieldChange('quiz', { ...content.quiz, questions: updatedQuestions });
  };

  const removeQuizQuestion = (questionId) => {
    const updatedQuestions = (content.quiz?.questions || []).filter(question => question.id !== questionId);
    handleFieldChange('quiz', { ...content.quiz, questions: updatedQuestions });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Play className="w-6 h-6 text-blue-500" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Éditeur de Présentation Interactive
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPreview(content)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Eye className="w-4 h-4" />
            Aperçu
          </button>
          <button
            onClick={handleSave}
            disabled={isValidating || errors.length > 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {isValidating ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Erreurs */}
      {errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Erreurs de validation
            </h3>
          </div>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Informations générales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Titre de la leçon *
          </label>
          <input
            type="text"
            value={content.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            placeholder="Ex: Les bases de React"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description *
          </label>
          <input
            type="text"
            value={content.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="Description de la présentation"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('slides')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'slides'
                ? 'border-primary text-primary dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Slides ({(content.slides || []).length})
          </button>
          <button
            onClick={() => setActiveTab('quiz')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'quiz'
                ? 'border-primary text-primary dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Quiz ({(content.quiz?.questions || []).length})
          </button>
          <button
            onClick={() => setActiveTab('sandbox')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sandbox'
                ? 'border-primary text-primary dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Code Sandbox
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'slides' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Slides de la présentation
            </h3>
            <button
              onClick={addSlide}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter une slide
            </button>
          </div>

          <div className="space-y-4">
            {(content.slides || []).map((slide, index) => (
              <div key={slide.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Slide {index + 1}
                  </h4>
                  <button
                    onClick={() => removeSlide(slide.id)}
                    className="p-1 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Titre
                      </label>
                      <input
                        type="text"
                        value={slide.title}
                        onChange={(e) => updateSlide(slide.id, { title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Contenu
                      </label>
                      <textarea
                        value={slide.content}
                        onChange={(e) => updateSlide(slide.id, { content: e.target.value })}
                        rows="4"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        URL de l'image
                      </label>
                      <input
                        type="url"
                        value={slide.image}
                        onChange={(e) => updateSlide(slide.id, { image: e.target.value })}
                        placeholder="https://example.com/image.jpg"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Code (optionnel)
                    </label>
                    <textarea
                      value={slide.code}
                      onChange={(e) => updateSlide(slide.id, { code: e.target.value })}
                      rows="8"
                      placeholder="// Code d'exemple..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm resize-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'quiz' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Questions du quiz
            </h3>
            <button
              onClick={addQuizQuestion}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter une question
            </button>
          </div>

          <div className="space-y-4">
            {(content.quiz?.questions || []).map((question, index) => (
              <div key={question.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    Question {index + 1}
                  </h4>
                  <button
                    onClick={() => removeQuizQuestion(question.id)}
                    className="p-1 text-red-500 hover:text-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Question
                    </label>
                    <input
                      type="text"
                      value={question.question}
                      onChange={(e) => updateQuizQuestion(question.id, { question: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Options de réponse
                    </label>
                    {question.options.map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center gap-2 mb-2">
                        <input
                          type="radio"
                          name={`correct-${question.id}`}
                          checked={question.correct === optionIndex}
                          onChange={() => updateQuizQuestion(question.id, { correct: optionIndex })}
                          className="text-primary"
                        />
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...question.options];
                            newOptions[optionIndex] = e.target.value;
                            updateQuizQuestion(question.id, { options: newOptions });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Explication
                    </label>
                    <textarea
                      value={question.explanation}
                      onChange={(e) => updateQuizQuestion(question.id, { explanation: e.target.value })}
                      rows="2"
                      placeholder="Expliquez pourquoi cette réponse est correcte..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'sandbox' && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Environnement de développement
          </h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Code de départ
            </label>
            <textarea
              value={content.sandbox?.starterCode || ''}
              onChange={(e) => handleFieldChange('sandbox', { 
                ...content.sandbox, 
                starterCode: e.target.value 
              })}
              rows="10"
              placeholder="// Code de départ pour les étudiants..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dépendances (séparées par des virgules)
            </label>
            <input
              type="text"
              value={(content.sandbox?.dependencies || []).join(', ')}
              onChange={(e) => handleFieldChange('sandbox', { 
                ...content.sandbox, 
                dependencies: e.target.value.split(',').map(dep => dep.trim()).filter(dep => dep)
              })}
              placeholder="react, react-dom, lodash"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveEditor;
